import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';

// Custom Link component that scrolls to top
const ScrollToTopLink = ({ to, children, ...props }: { to: string; children: React.ReactNode; [key: string]: any }) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <Link to={to} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
};

const FooterContainer = styled.footer`
  background-color: var(--primary-purple);
  color: white;
  padding: 3rem 0 1rem;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  padding: 0 2rem;
  justify-content: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 1rem;
  }
`;

const FooterSection = styled.div`
  text-align: center;
  
  h3 {
    font-family: 'Betrycia-Regular', serif;
    color: var(--primary-yellow);
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 0.6rem;
    line-height: 1.6;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const FooterLogo = styled.img`
  width: 200px;
  margin-bottom: 1rem;
`;

const CategoryColumns = styled.div`
  display: grid;
  grid-template-columns: max-content 1px max-content; /* add thin divider column */
  column-gap: 0.5rem;
  row-gap: 0.25rem;
  justify-content: center;
  align-items: stretch;
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;

  li { 
    margin-bottom: 0.5rem; 
  }

  a {
    color: white;
    text-decoration: none;
    font-size: 0.9rem;
    
    &:hover { color: var(--primary-yellow); }
  }
`;

const SimpleList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: center;

  li { margin-bottom: 0.5rem; }

  a {
    color: white;
    text-decoration: none;
    font-size: 0.95rem;

    &:hover { color: var(--primary-yellow); }
  }
`;

const CategoryDivider = styled.div`
  width: 1px;
  background-color: var(--primary-yellow);
  opacity: 0.5; /* reduce brightness of the divider */
`;

const ContactInfo = styled.div`
  margin-top: 1rem;
  text-align: center;
  
  a {
    color: white;
    text-decoration: none;
    
    &:hover {
      color: var(--primary-yellow);
    }
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: center;
  
  i { color: white; }
`;

const SocialIcon = styled.a`
  color: white;
  font-size: 1.5rem;
  transition: color 0.3s;
  
  i { color: white; }
  
  &:hover { color: var(--primary-yellow); }
`;

const CopyrightSection = styled.div`
  max-width: 1200px;
  margin: 2rem auto 0;
  padding: 1rem 2rem 0;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 0.9rem;
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        {/* Left: Brand Logo */}
        <FooterSection>
          <LogoContainer>
            <FooterLogo src="/FlourAndFancy_logo.PNG" alt="Flour and Fancy Logo" />
          </LogoContainer>
        </FooterSection>

        {/* Middle: Our Categories */}
        <FooterSection>
          <h3>Our Categories</h3>
          <CategoryColumns>
            <CategoryList>
              <li><ScrollToTopLink to="/category/banana-bread">Banana bread</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/category/pastries">Pastries</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/category/donuts">Donuts</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/category/puff-pastries">Puff pastries</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/category/sundaes">Sundaes</ScrollToTopLink></li>
            </CategoryList>
            <CategoryDivider aria-hidden="true" />
            <CategoryList>
              <li><ScrollToTopLink to="/category/cakes">Cakes</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/category/cupcakes">Cupcakes</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/category/fondant">Fondant</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/category/brownies">Brownies</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/category/cookies">Cookies</ScrollToTopLink></li>
            </CategoryList>
          </CategoryColumns>
        </FooterSection>

        {/* Middle Right: Quick Links */}
        <FooterSection>
          <h3>Quick Links</h3>
          <SimpleList>
            <li><ScrollToTopLink to="/about">About Us</ScrollToTopLink></li>
            <li><ScrollToTopLink to="/contact">Contact Us</ScrollToTopLink></li>
          </SimpleList>
        </FooterSection>

        {/* Right: Order Now */}
        <FooterSection>
          <h3>Order Now</h3>
          <ContactInfo>
            <p><a href="tel:+923708025503">+92 370 8025503</a></p>
            <p><a href="mailto:fancyandflour@gmail.com">fancyandflour@gmail.com</a></p>
          </ContactInfo>
          <SocialIcons>
            <SocialIcon href="https://www.facebook.com/share/1KNfQXMfev/" target="_blank" aria-label="Facebook">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 320 512">
                <path fill="currentColor" d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"/>
              </svg>
            </SocialIcon>
            <SocialIcon href="https://www.instagram.com/flour.andfancy.pk?igsh=MW44ZnhzNGQ4NmV4dg==" target="_blank" aria-label="Instagram">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 448 512">
                <path fill="currentColor" d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
              </svg>
            </SocialIcon>
            <SocialIcon href="https://wa.me/923708025503" target="_blank" aria-label="WhatsApp">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 448 512">
                <path fill="currentColor" d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
              </svg>
            </SocialIcon>
          </SocialIcons>
        </FooterSection>
      </FooterContent>
      
      <CopyrightSection>
        <p>Copyright © 2025 Flour &amp; Fancy • Powered by Reys Solutions</p>
      </CopyrightSection>
    </FooterContainer>
  );
};

export default Footer;